import React, { useState, useEffect, useContext, useMemo } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import axios from 'src/axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Popover,
  Snackbar,
  Tooltip,
  Typography
} from '@mui/material';
import {
  APICONSTANTS,
  CONSTANTS,
  PEDIGREE,
  PHENOTYPES_CONSTANTS
} from 'src/components/Constants';
import { SnackContext, ThemeContext } from 'src/store/ContextStore';
import { getPhenotypeSearchURL, getPersonAge } from 'src/util/util';
import { AppContext } from 'src/store/ContextStore';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { EVENTS } from 'src/components/admin/appReducer';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PhenotypeModal from './PhenotypeModal';
import { TILE_COLOURS } from '../tileColours';
import InlineRecordCapture from '../InlineRecordCapture';

// =================== PhenotypesMenu 主组件 ===================
const PhenotypesMenu = ({
  data,
  setMemberRecords,
  memberRecords,
  setPedigreeState,
  modificationHistoryHandle,
  onDirtyChange,
  saveRef,
  discardRef,
  isDisabled
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [page, setPage] = useState(PHENOTYPES_CONSTANTS['initialPage']);
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [fetchedPhenotypes, setFetchedPhenotypes] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPhenotype, setSelectedPhenotype] = useState(null);
  const [staffMemberNames, setStaffMemberNames] = useState([]);
  const [expandedNewIds, setExpandedNewIds] = useState(new Set());
  const [hoveredTileId, setHoveredTileId] = useState(null);
  // Confirmation popover for the Present/Absent toggle, anchored to the tile's
  // hover icon. Clicking the bare icon opens this; the flip only happens via
  // the button inside it.
  const [phenotypePopover, setPhenotypePopover] = useState({
    anchorEl: null,
    item: null
  });
  const [undoSnack, setUndoSnack] = useState({
    open: false,
    message: '',
    item: null,
    prev: null
  });

  useEffect(() => {
    axios.get('/clinical-notes/staff-members').then((res) => {
      setStaffMemberNames(res.data.map((m) => `${m.firstName} ${m.lastName}`));
    });
  }, []);

  const { setSnack } = useContext(SnackContext);
  const { content, dispatchUpdate } = useContext(AppContext);
  const { theme } = useContext(ThemeContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;

  const t = useAutoTranslation();

  let user_id = useMemo(() => {
    return user?.user_id || user?._id;
  }, [user]);

  let familyMemberId = useMemo(() => {
    return data?.familyMemberId;
  }, [data]);

  let proBandId = useMemo(() => {
    return data?.proBandId;
  }, [data]);

  useEffect(() => {
    onDirtyChange?.(selectedItems.length > 0);
  }, [selectedItems.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (discardRef)
      discardRef.current = () => {
        setSelectedItems([]);
        setSearchTerm('');
      };
  });

  const fetchDataByHPOTerm = async (currentPage) => {
    if (searchTerm.length >= 3) {
      let url = getPhenotypeSearchURL(searchTerm, currentPage);
      axios
        .get(url)
        .then(({ data }) => {
          if (isError) setIsError(false);

          const { terms = [], totalCount } = data || {};

          const newOptions =
            terms.map((d) => ({
              id: d.id,
              name: d.name,
              definition: d.definition,
              comment: d.comment,
              synonyms: d.synonyms
            })) || [];

          if (!currentPage) {
            setOptions([...newOptions]);
          } else {
            setOptions((preOption) => [...preOption, ...newOptions]);
          }

          setTotalCount(totalCount);
          setPage(currentPage);
        })
        .catch((err) => {
          setIsError(true);
          console.log('ERRRR-', err);
        });
    } else {
      setOptions([]);
    }
  };

  const getPhenotypes = () => {
    const id = proBandId ?? familyMemberId;
    if (!id || !user_id) return;

    // Always look up the freshest data from memberRecords first
    const currentMember = memberRecords.find((m) => m.pid === data?.pid);
    const memberPhenotypes =
      currentMember?.phenotypes || data?.phenotypes || [];

    // Always reset (clears stale data from a previously selected node)
    setFetchedPhenotypes(memberPhenotypes);

    axios
      .get(
        APICONSTANTS.getPhenotypesById
          .replace(':user_id', user_id)
          .replace(':familyMemberId', id),
        {
          params: { isProband: proBandId ? true : false }
        }
      )
      .then(({ data: responseData }) => {
        const { phenotypes = [] } = responseData || {};
        // Use API data if available; otherwise keep what was seeded from memberRecords
        if (phenotypes.length > 0) {
          setFetchedPhenotypes(phenotypes);
        }
      })
      .catch((err) => {
        console.log('ERRRR-', err);
      });
  };

  useEffect(() => {
    fetchDataByHPOTerm(PHENOTYPES_CONSTANTS['initialPage']);
    // eslint-disable-next-line
  }, [searchTerm]);

  useEffect(() => {
    getPhenotypes();
    setExpandedNewIds(new Set());
    // eslint-disable-next-line
  }, [familyMemberId, proBandId]);

  useEffect(() => {
    const memberData = proBandId
      ? users?.selectedUser
      : (users?.family || []).find((m) => m.familyMemberId === familyMemberId);
    const incoming = memberData?.phenotypes || [];
    if (!incoming.length) return;
    setFetchedPhenotypes((prev) => {
      const prevIds = new Set(prev.map((p) => p.id));
      if (incoming.every((p) => prevIds.has(p.id))) return prev;
      return incoming;
    });
  }, [users?.selectedUser?.phenotypes, users?.family]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTileClick = (phenotype) => {
    setSelectedPhenotype(phenotype);
    setModalOpen(true);
  };

  const updateData = (memberRecords) => {
    setMemberRecords([...memberRecords]);

    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));
  };

  const applyPhenotypeUpdate = (updated) => {
    setFetchedPhenotypes((prev) =>
      prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p))
    );
    const next = memberRecords.map((elem) => {
      if (elem.pid !== data?.pid) return elem;
      return {
        ...elem,
        phenotypes: (elem.phenotypes || []).map((p) =>
          p.id === updated.id ? { ...p, ...updated } : p
        )
      };
    });
    updateData(next);
  };

  const persistPhenotypeUpdate = (updated) => {
    const id = proBandId ?? familyMemberId;
    return axios.put(
      APICONSTANTS.updatePhenotype
        .replace(':user_id', user_id)
        .replace(':familyMemberId', id),
      { phenotype: updated, isProband: Boolean(proBandId) }
    );
  };

  const handleModalSave = (updatedFields) => {
    const updatedPhenotype = { ...selectedPhenotype, ...updatedFields };

    // Optimistically update BOTH fetchedPhenotypes (drives the right-hand
    // panel tile) AND memberRecords (drives the pedigree canvas labels).
    // Previously this only touched fetchedPhenotypes, so a Display-on-
    // Pedigree toggle from the modal didn't surface on the pedigree until
    // a manual refresh. applyPhenotypeUpdate handles both via updateData,
    // which also bumps PedigreeState.save → IN_PROGRESS so the renderer
    // picks up the change.
    applyPhenotypeUpdate(updatedPhenotype);

    persistPhenotypeUpdate(updatedPhenotype)
      .then((response) => {
        if (response.data.success) {
          setSnack({
            status: true,
            msg: 'Phenotype updated successfully!',
            severity: 'success'
          });
        } else {
          setSnack({
            status: true,
            msg: response.data.message || 'Error updating phenotype',
            severity: 'error'
          });
        }
      })
      .catch((err) => {
        console.error('Error updating phenotype:', err);
        setSnack({
          status: true,
          msg: 'Error updating phenotype',
          severity: 'error'
        });
      })
      .finally(() => {
        setModalOpen(false);
      });
  };

  // Clicking the tile's hover icon opens the confirmation popover — it never
  // mutates state directly (clinically meaningful change, too easy to misfire).
  const handleToggleObserved = (e, item) => {
    e.stopPropagation();
    setPhenotypePopover({ anchorEl: e.currentTarget, item });
  };

  // The actual flip, committed from the button inside the popover.
  const commitToggleObserved = (item) => {
    const prevObserved = item.observed ?? true;
    const prevDisplay = item.displayOnPedigree ?? true;
    const nextObserved = !prevObserved;
    const updated = {
      ...item,
      observed: nextObserved,
      displayOnPedigree: nextObserved
    };
    applyPhenotypeUpdate(updated);
    persistPhenotypeUpdate(updated).catch((err) => {
      console.error('Error updating phenotype:', err);
      applyPhenotypeUpdate({
        ...item,
        observed: prevObserved,
        displayOnPedigree: prevDisplay
      });
      setSnack({
        status: true,
        msg: 'Error updating phenotype',
        severity: 'error'
      });
    });
    setUndoSnack({
      open: true,
      message: `${item.name} ${t('marked as')} ${
        nextObserved ? t('Present') : t('Explicitly Absent')
      }.`,
      item,
      prev: { observed: prevObserved, displayOnPedigree: prevDisplay }
    });
    setPhenotypePopover({ anchorEl: null, item: null });
  };

  const handleUndoToggle = () => {
    if (!undoSnack.item || !undoSnack.prev) {
      setUndoSnack({ open: false, message: '', item: null, prev: null });
      return;
    }
    const reverted = {
      ...undoSnack.item,
      observed: undoSnack.prev.observed,
      displayOnPedigree: undoSnack.prev.displayOnPedigree
    };
    applyPhenotypeUpdate(reverted);
    persistPhenotypeUpdate(reverted).catch((err) => {
      console.error('Error reverting phenotype:', err);
      setSnack({
        status: true,
        msg: 'Error reverting phenotype',
        severity: 'error'
      });
    });
    setUndoSnack({ open: false, message: '', item: null, prev: null });
  };

  const onSave = (itemsOverride) => {
    const items = itemsOverride || selectedItems;
    const id = proBandId ?? familyMemberId;

    if (!id || !user_id) {
      return setSnack({
        status: true,
        msg: 'Please save the pedigree first',
        severity: 'error'
      });
    }

    if (items.length === 0) {
      return setSnack({
        status: true,
        msg: 'No phenotypes selected. Please add at least one phenotype to proceed.',
        severity: 'error'
      });
    }

    setLoading(true);

    const newIds = new Set(items.map((item) => item.id));
    setExpandedNewIds((prev) => new Set([...prev, ...newIds]));

    const selectedPhenotypes = items.map((item) => ({
      id: item.id,
      name: item.name
    }));

    const payload = {
      familyId: user.familyId,
      familyMemberId: id,
      user_id: user_id,
      isProband: proBandId ? true : false,
      phenotypes: selectedPhenotypes
    };

    return axios
      .post(APICONSTANTS.phenotypes, payload)
      .then((res) => {
        if (res.data?.success) {
          const new_dataset = memberRecords.map((elem) => {
            if (elem.pid === data.pid) {
              return {
                ...elem,
                phenotypes: res.data.phenotypes
              };
            }
            return elem;
          });

          updateData(new_dataset);

          setFetchedPhenotypes(res.data.phenotypes);
          dispatchUpdate({
            type: EVENTS.SET_PEDIGREE_PHENOTYPES,
            value: selectedPhenotypes
          });
          setSnack({
            status: true,
            msg: 'Phenotypes saved successfully',
            severity: 'success'
          });
          setSearchTerm('');
          setSelectedItems([]);
        } else {
          setSnack({
            status: true,
            msg: res.data.message || 'Error saving phenotypes',
            severity: 'error'
          });
        }
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: err.response?.data?.error || 'An error occurred!',
          severity: 'error'
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSelect = (event, value) => {
    if (value) {
      const newItems = [];
      value.forEach((newItem) => {
        const alreadySelected = selectedItems.some((m) => m.id === newItem.id);
        const alreadySaved = fetchedPhenotypes.some((f) => f.id === newItem.id);
        if (!alreadySelected && !alreadySaved) {
          newItems.push(newItem);
        }
      });
      if (newItems.length > 0) {
        onSave(newItems);
      }
      setSearchTerm('');
    }
  };

  const inlinePhenotypeSave = (updatedPhenotype) => {
    const id = proBandId ?? familyMemberId;
    setFetchedPhenotypes((prev) =>
      prev.map((item) =>
        item.id === updatedPhenotype.id ? updatedPhenotype : item
      )
    );
    return axios
      .put(
        APICONSTANTS.updatePhenotype
          .replace(':user_id', user_id)
          .replace(':familyMemberId', id),
        {
          phenotype: updatedPhenotype,
          isProband: Boolean(proBandId)
        }
      )
      .then((response) => {
        if (response.data.success) {
          setSnack({
            status: true,
            msg: 'Phenotype updated successfully!',
            severity: 'success'
          });
        }
      })
      .catch((err) => {
        console.error('Error updating phenotype:', err);
        setSnack({
          status: true,
          msg: 'Error updating phenotype',
          severity: 'error'
        });
      });
  };

  // Keep saveRef current so parent can trigger save with latest state
  useEffect(() => {
    if (saveRef) saveRef.current = onSave;
  });

  const handleScroll = (event) => {
    const target = event.target;
    if (
      target.scrollHeight - target.scrollTop === target.clientHeight &&
      options.length < totalCount
    ) {
      let newCurrentPage = page + 1;
      fetchDataByHPOTerm(newCurrentPage);
    }
  };

  const onDeletePhenotype = async (phenotypeId) => {
    const id = proBandId ?? familyMemberId;
    if (!id || !user_id) return;

    return axios
      .delete(
        APICONSTANTS.deletePhenotypeById
          .replace(':user_id', user_id)
          .replace(':familyMemberId', id),
        {
          data: {
            phenotypeId,
            isProband: proBandId ? true : false
          }
        }
      )
      .then((res) => {
        const new_dataset = memberRecords.map((elem) => {
          if (elem.pid === data.pid) {
            return {
              ...elem,
              phenotypes: elem.phenotypes.filter(
                (phenotype) => phenotype.id !== phenotypeId
              )
            };
          }
          return elem;
        });

        updateData(new_dataset);

        // Recalculate global unique phenotype list from new_dataset
        const allPhenotypes = new_dataset.flatMap(
          (item) => item.phenotypes || []
        );

        dispatchUpdate({
          type: EVENTS.UPDATE_GLOBAL_PHENOTYPES,
          value: allPhenotypes
        });

        setFetchedPhenotypes(
          fetchedPhenotypes.filter((phenotype) => phenotype.id !== phenotypeId)
        );
        setSnack({
          status: true,
          msg: 'Phenotypes deleted successfully',
          severity: 'success'
        });
        setModalOpen(false);
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: err.response?.data?.message ?? 'An error has occurred!',
          severity: 'error'
        });
      });
  };

  return (
    <>
      <Grid container spacing={2}>
        {isError && (
          <Grid size={12}>
            <Alert severity="warning">
              {t(
                'The terminology service is unreachable. Please contact admin.'
              )}
            </Alert>
          </Grid>
        )}

        <Grid size={12}>
          <Autocomplete
            multiple
            clearOnBlur={false}
            id="tags-outlined"
            options={options}
            value={[]}
            getOptionLabel={(option) => option.name}
            onChange={handleSelect}
            disabled={isDisabled}
            filterOptions={(options) => options}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search for Phenotypes"
                data-va-anchor="pedigree.phenotypes.search"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            )}
            slotProps={{
              listbox: { onScroll: handleScroll }
            }}
          />
        </Grid>
        {(() => {
          const all = [
            ...fetchedPhenotypes,
            ...selectedItems.filter(
              (s) => !fetchedPhenotypes.some((f) => f.id === s.id)
            )
          ];
          return [
            ...all.filter((p) => p.observed !== false),
            ...all.filter((p) => p.observed === false)
          ];
        })().map((item) => {
          const isSaved = fetchedPhenotypes.some((f) => f.id === item.id);
          const isExpanded = isSaved && expandedNewIds.has(item.id);
          const isAbsent = item.observed === false;
          const tileColours = isAbsent
            ? {
                background: '#fafafa',
                border: '#bdbdbd',
                accent: '#bdbdbd',
                text: 'rgba(0, 0, 0, 0.87)',
                subtext: '#757575'
              }
            : TILE_COLOURS.phenotype;
          return (
            <React.Fragment key={item._id || item.id}>
              <Grid size={12}>
                <div
                  style={{
                    border: `1px solid ${tileColours.border}`,
                    borderLeft: `3px solid ${tileColours.accent}`,
                    padding: '8px 10px',
                    borderRadius: isExpanded ? '6px 6px 0 0' : '6px',
                    backgroundColor: tileColours.background,
                    cursor: isSaved && !isExpanded ? 'pointer' : 'default',
                    marginBottom: isExpanded ? 0 : undefined,
                    ...(isSaved
                      ? {}
                      : {
                          backgroundColor: '#FFF3E0',
                          color: '#BA7517',
                          border: '1px solid #BA7517',
                          borderLeft: '3px solid #BA7517'
                        }),
                    ...(isExpanded ? { borderBottom: 'none' } : {})
                  }}
                  onClick={() =>
                    isSaved && !isExpanded && handleTileClick(item)
                  }
                  onMouseEnter={() => setHoveredTileId(item.id)}
                  onMouseLeave={() =>
                    setHoveredTileId((curr) => (curr === item.id ? null : curr))
                  }
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: tileColours.text
                        }}
                      >
                        {item.name}
                      </Typography>
                      {isAbsent && (
                        <Chip
                          label={t('Explicitly Absent')}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 18,
                            borderRadius: '3px',
                            fontWeight: 500,
                            bgcolor: '#fff',
                            borderColor: 'grey.600',
                            color: 'grey.900',
                            '& .MuiChip-label': { px: 0.75, fontSize: 11 }
                          }}
                        />
                      )}
                    </Box>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {isSaved && (
                        <Tooltip
                          open={
                            phenotypePopover.item?.id === item.id &&
                            phenotypePopover.anchorEl
                              ? false
                              : undefined
                          }
                          title={
                            isAbsent
                              ? t('Mark as present')
                              : t('Mark as Explicitly Absent')
                          }
                        >
                          <IconButton
                            size="small"
                            disabled={isDisabled}
                            onClick={(e) => handleToggleObserved(e, item)}
                            sx={{
                              p: 0.25,
                              opacity: hoveredTileId === item.id ? 1 : 0,
                              transition: 'opacity 120ms ease',
                              color: tileColours.subtext,
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                          >
                            {isAbsent ? (
                              <VisibilityIcon sx={{ fontSize: 18 }} />
                            ) : (
                              <VisibilityOffIcon sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      {item.comment && (
                        <Tooltip
                          title={
                            <Box sx={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                              {item.comment}
                            </Box>
                          }
                        >
                          <StickyNote2OutlinedIcon
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              color: tileColours.accent,
                              fontSize: 18,
                              cursor: 'help'
                            }}
                          />
                        </Tooltip>
                      )}
                      {item.clinicallyConfirmed && (
                        <CheckCircleOutlineIcon
                          style={{ color: 'green', fontSize: 20 }}
                        />
                      )}
                    </Box>
                  </div>
                  <Typography
                    variant="body2"
                    sx={{ color: tileColours.subtext }}
                  >
                    {item.id}
                  </Typography>
                </div>
                {isSaved && (
                  <Collapse in={isExpanded} unmountOnExit>
                    <InlineRecordCapture
                      record={item}
                      patientDob={data?.dob}
                      maxAge={getPersonAge(data)}
                      dateField="dateRecorded"
                      ageField="recordedAge"
                      dateFormat={theme.dateFormat}
                      dateLabel="Date Observed"
                      ageLabel="Age When Observed"
                      onSaveCapture={(update) => inlinePhenotypeSave(update)}
                      onSkip={() => {
                        setExpandedNewIds((prev) => {
                          const next = new Set(prev);
                          next.delete(item.id);
                          return next;
                        });
                      }}
                    />
                  </Collapse>
                )}
              </Grid>
            </React.Fragment>
          );
        })}

        <Grid size={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            {fetchedPhenotypes.some((p) => p.clinicallyConfirmed) ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <CheckCircleOutlineIcon
                  style={{ color: 'green', fontSize: 20 }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 'bold'
                  }}
                >
                  Clinically Confirmed
                </Typography>
              </Box>
            ) : (
              <span />
            )}
            <IconButton
              size="small"
              onClick={() =>
                modificationHistoryHandle(CONSTANTS.auditLogType['phenotypes'])
              }
            >
              <HistoryIcon />
            </IconButton>
          </Box>
        </Grid>

        <Snackbar
          open={undoSnack.open}
          autoHideDuration={6000}
          onClose={(_, reason) => {
            if (reason === 'clickaway') return;
            setUndoSnack({ open: false, message: '', item: null, prev: null });
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity="info"
            variant="filled"
            sx={{ alignItems: 'center' }}
            action={
              <>
                <Button color="inherit" size="small" onClick={handleUndoToggle}>
                  {t('Undo')}
                </Button>
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() =>
                    setUndoSnack({
                      open: false,
                      message: '',
                      item: null,
                      prev: null
                    })
                  }
                  sx={{ ml: 0.5 }}
                >
                  <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>
                    ×
                  </Box>
                </IconButton>
              </>
            }
          >
            {undoSnack.message}
          </Alert>
        </Snackbar>

        <Popover
          open={Boolean(phenotypePopover.anchorEl)}
          anchorEl={phenotypePopover.anchorEl}
          onClose={() => setPhenotypePopover({ anchorEl: null, item: null })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {phenotypePopover.item &&
            (() => {
              const it = phenotypePopover.item;
              const present = it.observed !== false;
              return (
                <Box sx={{ p: 2, width: 260 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: present
                        ? TILE_COLOURS.phenotype.text
                        : 'rgba(0, 0, 0, 0.87)'
                    }}
                  >
                    {it.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: 'text.secondary',
                      mt: 0.5,
                      mb: 1.5
                    }}
                  >
                    {present
                      ? t(
                          'Marking as Absent will hide this phenotype from the pedigree.'
                        )
                      : t(
                          'Marking as Present will show this phenotype on the pedigree by default.'
                        )}
                  </Typography>
                  <Button
                    fullWidth
                    autoFocus
                    variant="contained"
                    onClick={() => commitToggleObserved(it)}
                    sx={{
                      bgcolor: present ? '#757575' : '#2BA9BF',
                      '&:hover': { bgcolor: present ? '#616161' : '#22919f' }
                    }}
                  >
                    {present ? t('Mark as Absent') : t('Mark as Present')}
                  </Button>
                </Box>
              );
            })()}
        </Popover>

        {selectedPhenotype && (
          <PhenotypeModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            phenotype={selectedPhenotype}
            onSave={handleModalSave}
            user={user}
            onDeletePhenotype={onDeletePhenotype}
            isDisabled={isDisabled}
            patientDob={data?.dob}
            staffMemberNames={staffMemberNames}
            onShowHistory={
              selectedPhenotype?.id && modificationHistoryHandle
                ? () =>
                    modificationHistoryHandle(
                      CONSTANTS.auditLogType.phenotypes,
                      {},
                      selectedPhenotype.id
                    )
                : null
            }
          />
        )}
      </Grid>
    </>
  );
};

export default PhenotypesMenu;
